<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" max-width=568 persistent fluid scrollable content-class="round-2xl")
  v-card
    v-card-title.pa-4
      v-spacer
      .text-xl.font-bold.ml-10 {{ $t('payment') }}
      v-spacer
      v-btn(icon @click="showDialog = false" small color="primary")
        v-icon mdi-close
    v-divider

    v-card-text.primary--text.px-6.pt-6.pb-10
      .text-sm.font-semibold.neutral500--text {{ $t('payment_method') }}
      y-select.mt-2(v-model="selectedPayment.panel" :items="panels" item-text="name" item-value="id" :placeholder="$t('select_payment_method')" :loading="isFetchingPanel" @input="onInputPayment")

      yz-card.pa-4.mt-4(v-if="selectedPayment.panel" color="neutral-200")
        template(v-if="isPanelRemarkType")
          .yz-medium-body-xs.text-neutral-500 {{ $t('panel.remarks') }}
          .yz-regular-body-sm {{ panelRemarkText }}

        template(v-else)
          v-row(no-gutters align="center")
            v-col
              .yz-medium-body-xs.text-neutral-500 {{ $t('panel.verification_link') }}
            v-col.shrink
              yz-btn(size="sm" :label="$t('panel.open_link')" :disabled="!getPanelLink(selectedPayment.panel)" @click="onOpenLink(getPanelLink(selectedPayment.panel))")

        yz-divider.my-4

        v-row(no-gutters)
          v-col
            .yz-medium-body-sm {{ $t('panel.staff_id') }}
          v-col.shrink
            yz-switch(v-model="showStaffId" size="sm")
          v-col(v-if="showStaffId" cols="12")
            yz-text-field.mt-1(v-model="staffId" :placeholder="$t('panel.enter_staff_id')")

        v-row.mt-3(no-gutters)
          v-col
            .yz-medium-body-sm {{ $t('panel.chit_number') }}
          v-col.shrink
            yz-switch(v-model="showChitNumber" size="sm")
          v-col(v-if="showChitNumber" cols="12")
            yz-text-field.mt-1(v-model="chitNumber" :placeholder="$t('panel.enter_chit_number')")

        .yz-medium-body-sm.mt-3 Panel note
        yz-textarea.mt-1(v-model="selectedPayment.note" placeholder="Enter panel note" background-color="neutral100" outlined rows="3" hide-details="auto")

    v-divider
    v-card-actions.py-4.px-6
      v-btn.button-md.underline.px-0(@click="showDialog = false" text) {{ $t('cancel') }}
      v-spacer
      v-btn.button-md(@click="save" :loading="isLoading" color="primary" :disabled="isFetchingPanel" depressed) {{ $t('save') }}

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import clone from 'just-clone'

export default {
  name: 'EditPaymentDialog',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    payment: {
      type: Object,
      default: null
    },
    pid: {
      type: String,
      required: true,
    },
    patient: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    isLoading: false,
    isFetchingPanel: false,
    selectedPayment: {
      panel: null,
      type: 'panel'
    },
    panels: [],
    showStaffId: false,
    staffId: '',
    showChitNumber: false,
    chitNumber: ''
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    showDialog: {
      get () {
        return this.open 
      },
      set (newValue) {
        this.$emit('update:open', newValue)
      }
    },
    selectedPanelData () {
      const found = this.panels.find(panel => panel.id === this.selectedPayment.panel)
      if (found) return found
      return null
    },
    isPanelRemarkType () {
      return this.selectedPanelData?.display_remarks ?? false
    },
    panelRemarkText () {
      return this.selectedPanelData?.remarks && this.selectedPanelData.remarks !== '' ? this.selectedPanelData.remarks : '-'
    }
  },
  mounted () {
    if (this.payment.panel) {
      this.selectedPayment = clone(this.payment)
      this.selectedPayment.panel = this.payment.panel.id
    }
    // Initialize staff ID and chit number from existing payment data
    if (this.payment?.staff_id) {
      this.staffId = this.payment.staff_id
      this.showStaffId = true
    }
    if (this.payment?.chit_number) {
      this.chitNumber = this.payment.chit_number
      this.showChitNumber = true
    }
    this.fetchPanel()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchPanel () {
      this.isFetchingPanel = true
      let params = {
        company: this.isHq ? this.hqSelectedCompany.id : this.company.id,
        enabled: true,
        channel: 'panel',
        page_size: -1,
        ordering: 'name'
      }
      this.$axios.get('/payment_methods', { params })
        .then(resp => {
          if (resp.data) {
            let data = resp.data.results ?? []
            data.unshift({ id: null, name: 'Self pay', verification_link: null})
            this.panels = data
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingPanel = false
        })
    },
    getPanelLink (id) {
      if (id && this.panels.find(f => f.id === id)) {
        return this.panels.find(f => f.id === id).verification_link
      }
      return ''
    },
    onOpenLink (link) {
      window.open(link, '_blank')
    },
    onInputPayment() {
      // Clear existing staff ID, chit number, and note
      this.staffId = ''
      this.chitNumber = ''
      this.selectedPayment.note = ''
      this.showStaffId = false
      this.showChitNumber = false

      // Priority 1: If user selects back the original panel from props, restore original values
      if (this.payment?.panel?.id === this.selectedPayment.panel) {
        if (this.payment.staff_id) {
          this.staffId = this.payment.staff_id
          this.showStaffId = true
        }
        if (this.payment.chit_number) {
          this.chitNumber = this.payment.chit_number
          this.showChitNumber = true
        }
        if (this.payment.note) {
          this.selectedPayment.note = this.payment.note
        }
        return
      }

      // Priority 2: Auto-populate from patient's panel history
      if (this.patient?.panels && this.patient.panels.length > 0 && this.selectedPayment.panel) {
        const matchingPanel = this.patient.panels.find(p => p.panel.payment_method === this.selectedPayment.panel)
        if (matchingPanel) {
          if (matchingPanel.staff_id) {
            this.staffId = matchingPanel.staff_id
            this.showStaffId = true
          }
          if (matchingPanel.last_visit_note) {
            this.selectedPayment.note = matchingPanel.last_visit_note
          }
        }
      }
    },
    save () {
      this.isLoading = true
      const params = {
        default_payment: this.selectedPayment
      }
      if (!this.selectedPayment.panel) {
        params.default_payment = {
          type: 'self-pay',
          note: ''
        }
      } else {
        // Include staff_id and chit_number if toggles are enabled
        if (this.showStaffId) {
          params.default_payment.staff_id = this.staffId || null
        }
        if (this.showChitNumber) {
          params.default_payment.chit_number = this.chitNumber || null
        }
      }
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.patch(`/clinics/${companySlug}/registrations/${this.pid}`, params)
        .then(resp => {
          this.showMessage(['success', this.$t('success_message.successfully_updated')])
          this.$emit('updated', resp.data.default_payment)
          this.showDialog = false
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>
